import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, MessageCircle } from "lucide-react";
import { LOGO_URL, CONTACTS, waLink } from "@/data/site";

const LINKS = [
  { label: "Perché noi", href: "#perche" },
  { label: "Corsi", href: "#corsi" },
  { label: "Come funziona", href: "#come-funziona" },
  { label: "Sedi", href: "#sedi" },
  { label: "FAQ", href: "#faq" },
  { label: "Contatti", href: "#contatti" },
];

export const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (href) => {
    setOpen(false);
    document.querySelector(href)?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <motion.header
      data-testid="site-header"
      initial={{ y: -80 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.6, ease: "easeOut" }}
      className={`fixed top-0 inset-x-0 z-50 transition-colors duration-300 ${
        scrolled
          ? "bg-ink-900/80 backdrop-blur-xl border-b border-white/10"
          : "bg-transparent"
      }`}
    >
      <div className="mx-auto max-w-7xl px-5 sm:px-8 h-20 flex items-center justify-between">
        <button
          data-testid="logo-home"
          onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          className="flex items-center gap-3 group"
        >
          <img src={LOGO_URL} alt="Trinity Musical School" className="h-12 w-auto object-contain rounded-md" />
          <div className="text-left leading-tight">
            <div className="font-serif text-lg text-white tracking-wide">Trinity Musical School</div>
            <div className="text-[10px] uppercase tracking-[0.25em] text-gold-500">Amici per la Musica APS</div>
          </div>
        </button>

        <nav className="hidden lg:flex items-center gap-9">
          {LINKS.map((l) => (
            <button
              key={l.href}
              data-testid={`nav-${l.href.replace("#", "")}`}
              onClick={() => go(l.href)}
              className="text-sm text-gray-300 hover:text-gold-500 transition-colors duration-200"
            >
              {l.label}
            </button>
          ))}
          <a
            data-testid="header-preiscrizione-btn"
            href="#contatti"
            onClick={(e) => {
              e.preventDefault();
              go("#contatti");
            }}
            className="rounded-full bg-gold-500 text-ink-900 text-sm font-semibold px-6 py-2.5 hover:bg-gold-400 hover:-translate-y-0.5 transition-transform duration-200 gold-glow"
          >
            Contattaci
          </a>
        </nav>

        <button
          data-testid="mobile-menu-toggle"
          className="lg:hidden text-white"
          onClick={() => setOpen(!open)}
        >
          {open ? <X size={26} /> : <Menu size={26} />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="lg:hidden overflow-hidden bg-ink-900/95 backdrop-blur-xl border-b border-white/10"
          >
            <div className="px-5 py-6 flex flex-col gap-4">
              {LINKS.map((l) => (
                <button
                  key={l.href}
                  data-testid={`mobile-nav-${l.href.replace("#", "")}`}
                  onClick={() => go(l.href)}
                  className="text-left text-gray-200 py-1"
                >
                  {l.label}
                </button>
              ))}
              <a
                data-testid="mobile-preiscrizione-btn"
                href="#contatti"
                onClick={(e) => {
                  e.preventDefault();
                  go("#contatti");
                }}
                className="mt-2 rounded-full bg-gold-500 text-ink-900 font-semibold px-6 py-3 text-center"
              >
                Contattaci
              </a>
              <a
                href={waLink("Ciao, vorrei informazioni sulle preiscrizioni 2026/2027")}
                target="_blank"
                rel="noreferrer"
                className="flex items-center justify-center gap-2 text-gold-500 py-1"
              >
                <MessageCircle size={18} /> WhatsApp
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
};
